import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, useWindowDimensions } from 'react-native';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { Clickable } from '../../components/common/Clickable';
import { type ThemeColors, appTheme } from '../../shared/theme';
import { useTranslation, getApiText, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { resolveWebLayoutWidth } from '../../shared/platform/webLayout';
import {
  getUniqueSortedDaysFromSection,
  resolveBannerPopupImageUri,
  type BannerSlideMetrics,
  type CategoryDayEntry,
  type CategoryDaysItem,
  type PlanItem,
  type PlanSection,
} from '../../data/domains/subscription';
import { type SubscriptionListStyles } from './subscriptionListStyles';
import { SubscriptionCatalogPlanCard } from './SubscriptionCatalogPlanCard';
import type { TagChipItem } from './subscriptionListTypes';

type BannerMetricChip = {
  key: string;
  label: string;
  tone: 'primary' | 'outline' | 'tertiary' | 'muted';
};

function buildBannerMetricChips(
  metrics: BannerSlideMetrics | null,
  t: (key: string, params?: Record<string, string | number>) => string,
): BannerMetricChip[] {
  if (metrics == null) {
    return [];
  }
  const chips: BannerMetricChip[] = [];
  if (metrics.plan_count > 0) {
    chips.push({
      key: 'plans',
      label: t('subscriptions.bannerMetricPlans').replace('{{count}}', formatNumber(metrics.plan_count)),
      tone: 'primary',
    });
  }
  if (metrics.duration_count > 0) {
    chips.push({
      key: 'durations',
      label: t('subscriptions.bannerMetricDurations').replace('{{count}}', formatNumber(metrics.duration_count)),
      tone: 'outline',
    });
  }
  if (metrics.min_days != null && metrics.max_days != null) {
    chips.push({
      key: 'days',
      label: t('subscriptions.bannerMetricDaysRange')
        .replace('{{min}}', formatNumber(metrics.min_days))
        .replace('{{max}}', formatNumber(metrics.max_days)),
      tone: 'tertiary',
    });
  }
  if (metrics.category_count > 0) {
    chips.push({
      key: 'categories',
      label: t('subscriptions.bannerMetricCategories').replace('{{count}}', formatNumber(metrics.category_count)),
      tone: 'muted',
    });
  }
  return chips;
}

export function BannerPopupModal({
  visible,
  bannerData,
  metrics,
  fallback_image_uri,
  onClose,
  styles,
  themeColors,
}: {
  visible: boolean;
  bannerData: TagChipItem | CategoryDaysItem | null;
  metrics: BannerSlideMetrics | null;
  fallback_image_uri?: string | null;
  onClose: () => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
}) {
  const { t } = useTranslation();
  const { textAlign, writingDirection, flipRowStyle } = useRtl();
  const { width: window_width } = useWindowDimensions();
  const layout_width = resolveWebLayoutWidth(window_width);
  const image_width = Math.max(0, layout_width - appTheme.spacing.md * 2);

  const banner_record = bannerData as unknown as Record<string, unknown> | null;
  const image_uri = useMemo(
    () => (banner_record ? resolveBannerPopupImageUri(banner_record, fallback_image_uri) : null),
    [banner_record, fallback_image_uri],
  );
  const title = useMemo(() => {
    if (!banner_record) {
      return '';
    }
    return (
      getApiText(banner_record, 'title', 'arabic_title') ||
      (typeof banner_record.name === 'string' ? banner_record.name : '') ||
      ''
    );
  }, [banner_record]);
  const description = useMemo(() => {
    if (!banner_record) {
      return null;
    }
    return (
      getApiText(banner_record, 'description', 'arabic_description') ||
      (typeof banner_record.description === 'string' ? banner_record.description : null) ||
      (typeof banner_record.arabic_description === 'string' ? banner_record.arabic_description : null)
    );
  }, [banner_record]);
  const metric_chips = useMemo(() => buildBannerMetricChips(metrics, t), [metrics, t]);
  const has_body_content = Boolean(description) || metric_chips.length > 0;

  if (!visible || !bannerData) {
    return null;
  }

  const chip_tone_style = (tone: BannerMetricChip['tone']) => {
    switch (tone) {
      case 'primary':
        return {
          wrap: { backgroundColor: themeColors.primary, borderColor: themeColors.primary },
          text: { color: themeColors.primaryText },
        };
      case 'outline':
        return {
          wrap: { backgroundColor: themeColors.primaryText, borderColor: themeColors.primary },
          text: { color: themeColors.primary },
        };
      case 'tertiary':
        return {
          wrap: { backgroundColor: themeColors.tertiary, borderColor: themeColors.tertiary },
          text: { color: themeColors.tertiaryText },
        };
      default:
        return {
          wrap: {
            backgroundColor: themeColors.surfaceElevated,
            borderColor: themeColors.border,
          },
          text: { color: themeColors.textSecondary },
        };
    }
  };

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={onClose}
      title={title || t('subscriptions.seeMore')}
      animationType="slide"
      showDragHandle
      sheetHeightMode="fitContent"
      sheetStyle={{ maxHeight: '88%', backgroundColor: themeColors.backgroundElevated, width: layout_width }}>
      <ScrollView
        style={styles.detailSheetScroll}
        contentContainerStyle={styles.detailSheetScrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        {image_uri ? (
          <View style={[styles.detailImageWrap, { width: image_width, alignSelf: 'center' }]}>
            <Image
              source={{ uri: image_uri }}
              style={[styles.detailImage, { width: image_width }]}
              resizeMode="cover"
              fadeDuration={0}
            />
          </View>
        ) : null}
        <View style={styles.detailBody}>
          {description ? (
            <Text style={[styles.detailDescription, { textAlign, writingDirection }]}>{description}</Text>
          ) : null}
          {metric_chips.length > 0 ? (
            <View style={styles.detailMetricsCard}>
              <Text style={[styles.detailMetricsHeading, { textAlign, writingDirection }]}>
                {t('subscriptions.plans')}
              </Text>
              <View style={[styles.detailMetricsChips, flipRowStyle]}>
                {metric_chips.map(chip => {
                  const tone = chip_tone_style(chip.tone);
                  return (
                    <View key={chip.key} style={[styles.detailMetricChip, tone.wrap]}>
                      <Text style={[styles.detailMetricChipText, tone.text]} numberOfLines={1}>
                        {chip.label}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>
          ) : null}
          {!has_body_content ? (
            <Text style={[styles.detailEmptyHint, { textAlign, writingDirection }]}>{t('subscriptions.pickAPlan')}</Text>
          ) : null}
        </View>
      </ScrollView>
    </BottomSheetPopup>
  );
}

export function CategoryPlanModal({
  visible,
  section,
  selectedDay,
  onSelectDay,
  onSelectPlan,
  onClose,
  onDismissComplete,
  styles,
  themeColors,
}: {
  visible: boolean;
  section: PlanSection | null;
  selectedDay: number | null;
  onSelectDay: (day: number) => void;
  onSelectPlan: (item: PlanItem) => void;
  onClose: () => void;
  onDismissComplete?: () => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
}) {
  const { t } = useTranslation();
  const { width: window_width, height: window_height } = useWindowDimensions();
  const layout_width = resolveWebLayoutWidth(window_width);
  const sheet_height = Math.round(window_height * 0.85);

  const unique_days = useMemo(() => getUniqueSortedDaysFromSection(section), [section]);
  const plans_for_day = useMemo(() => (section?.data && selectedDay != null ? section.data.filter(item => item.days === selectedDay) : []), [section, selectedDay]);

  const category_title = section ? getApiText(section as unknown as Record<string, unknown>, 'title', 'arabic_title') || section.title : '';

  if (!section) {
    return null;
  }

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={onClose}
      onDismissComplete={onDismissComplete}
      title={category_title}
      animationType="slide"
      showDragHandle
      sheetStyle={StyleSheet.flatten([
        styles.categoryPopupSheet,
        { height: sheet_height, width: layout_width },
      ])}>
      <View style={styles.categoryDayBarScrollContent}>
        {unique_days.map(day => {
          const is_selected = selectedDay === day;
          return (
            <Clickable
              key={day}
              style={[styles.categoryDayPill, is_selected && styles.categoryDayPillActive]}
              onPress={() => onSelectDay(day)}
              useNativePressable
              pressAnimated={false}
              haptics
              accessibilityLabel={`${day} ${t('subscriptions.daysLabel')}`}
              accessibilityRole="button"
              accessibilityState={{ selected: is_selected }}>
              <View style={styles.categoryDayPillInner}>
                <Text style={[styles.categoryDayPillText, is_selected && styles.categoryDayPillTextActive]}>
                  {day} {t('subscriptions.daysLabel')}
                </Text>
              </View>
            </Clickable>
          );
        })}
      </View>

      <ScrollView style={styles.categoryPopupPlansScroll} contentContainerStyle={styles.categoryPopupPlansScrollInner} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
        {plans_for_day.map(item => (
          <SubscriptionCatalogPlanCard key={item.key} item={item} colors={themeColors} styles={styles} onSelect={() => onSelectPlan(item)} />
        ))}
      </ScrollView>
    </BottomSheetPopup>
  );
}

export function CategoryFlowDayPlansModal({
  visible,
  dayEntry,
  categoryTitle,
  onSelectPlan,
  onClose,
  onDismissComplete,
  styles,
  themeColors,
}: {
  visible: boolean;
  dayEntry: CategoryDayEntry | null;
  categoryTitle: string;
  onSelectPlan: (item: PlanItem) => void;
  onClose: () => void;
  onDismissComplete?: () => void;
  styles: SubscriptionListStyles;
  themeColors: ThemeColors;
}) {
  const { t } = useTranslation();
  const { width: window_width, height: window_height } = useWindowDimensions();
  const layout_width = resolveWebLayoutWidth(window_width);
  const sheet_height = Math.round(window_height * 0.85);

  if (!dayEntry) {
    return null;
  }
  const day_val = dayEntry.day ?? dayEntry.type_key ?? 0;

  const category_flow_sheet_title = `${categoryTitle} · ${day_val} ${t('subscriptions.daysLabel')}`;

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={onClose}
      onDismissComplete={onDismissComplete}
      title={category_flow_sheet_title}
      animationType="slide"
      showDragHandle
      sheetStyle={StyleSheet.flatten([
        styles.categoryPopupSheet,
        { height: sheet_height, width: layout_width },
      ])}>
      <ScrollView style={styles.categoryPopupPlansScroll} contentContainerStyle={styles.categoryPopupPlansScrollInner} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
        {(dayEntry.tagged_plans ?? []).map(tagged => (
          <View key={tagged.tag_id} style={styles.categoryFlowTagGroup}>
            <Text style={styles.categoryFlowTagGroupTitle}>{tagged.tag_name}</Text>
            {(tagged.plans ?? []).map((plan: any, index: number) => {
              const plan_item: PlanItem = {
                key: `catflow-${day_val}-${tagged.tag_id}-${plan.subscription_id ?? plan.id}`,
                plan: { ...plan, days: day_val, type_key: day_val },
                days: day_val,
                categoryTitle,
                categoryImage: (plan as any).thumbnail ?? (plan as any).attachment,
              };
              return (
                <SubscriptionCatalogPlanCard key={plan_item.key} item={plan_item} colors={themeColors} styles={styles} onSelect={() => onSelectPlan(plan_item)} />
              );
            })}
          </View>
        ))}
      </ScrollView>
    </BottomSheetPopup>
  );
}
